import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import ProductCard from './ProductCard';
import SearchBar from './SearchBar';
import FilterBar from './FilterBar';
import '../styles/ProductCatalog.css';

const ProductCatalog = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [categories, setCategories] = useState([]);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  //tomo del environment
  const [itemsPerPage] = useState(process.env.REACT_APP_ITEMS_PER_PAGE || 20); // 20 productos por página
  
  // Ref para hacer scroll a la sección de productos
  const productsStartRef = useRef(null);

  // Leer categoría de la URL al cargar
  useEffect(() => {
    const categoryFromUrl = searchParams.get('categoria');
    if (categoryFromUrl) {
      setSelectedCategory(decodeURIComponent(categoryFromUrl));
    }
  }, [searchParams]);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reiniciar a la primera página cuando cambien los filtros
  }, [products, searchTerm, selectedCategory, sortBy]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Cargar productos y categorías en paralelo
      const [productsData, categoriesData] = await Promise.all([
        apiClient.get('/ecommerce/product'),
        apiClient.get('/ecommerce/category')
      ]);
      
      if (Array.isArray(productsData)) {
        ///reordeno productos prioridad los que tienen imagenes
        const productsWithImages = productsData.filter(p => p.imagenes && p.imagenes.length > 0);
        const productsWithoutImages = productsData.filter(p => !p.imagenes || p.imagenes.length === 0);
        const products = [...productsWithImages, ...productsWithoutImages];
        setProducts(products);
      } else {
        setError('Formato de respuesta inválido');
      }
      
      if (Array.isArray(categoriesData)) {
        // Extraer nombres de categorías dependiendo del formato de respuesta
        const categoryNames = categoriesData
          .map(cat => {
            // Si es un objeto (no null)
            if (typeof cat === 'object' && cat !== null) {
              return cat.nombre || cat.categoria || cat.name || '';
            }
            // Si es un string
            if (typeof cat === 'string') {
              return cat;
            }
            // Si no es válido, retornar cadena vacía
            return '';
          })
          .filter(c => c && c.trim() !== ''); // Filtrar valores vacíos
        
        setCategories(categoryNames);
      }
    } catch (err) {
      setError('Error al cargar los productos. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Filtro de productos con precio 0 (según configuración)
    const hideZeroPriceProducts = process.env.REACT_APP_HIDE_ZERO_PRICE_PRODUCTS === 'true';
    if (hideZeroPriceProducts) {
      filtered = filtered.filter(product => product.precio && product.precio > 0);
    }

    // Filtro de búsqueda
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.descripcion?.toLowerCase().includes(searchLower) ||
        product.categoria?.toLowerCase().includes(searchLower)
      );
    }

    // Filtro de categoría
    if (selectedCategory) {
      filtered = filtered.filter(product => product.categoria === selectedCategory);
    }

    // Ordenamiento
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.precio - b.precio);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.precio - a.precio);
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.descripcion.localeCompare(b.descripcion));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.descripcion.localeCompare(a.descripcion));
        break;
      default:
        // Orden predeterminado
        break;
    }

    setFilteredProducts(filtered);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Calcular productos de la página actual
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Funciones de navegación
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    
    // Scroll suave hasta donde comienzan los productos
    if (productsStartRef.current) {
      // Usar scrollIntoView para ir al inicio de los productos
      productsStartRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
      
      // Backup: intentar con offset manual si scrollIntoView no funciona bien
      setTimeout(() => {
        if (productsStartRef.current) {
          const elementPosition = productsStartRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px de margen superior
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <div className="catalog-loading">
        <div className="spinner"></div>
        <p>Cargando productos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="catalog-error">
        <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3>Error al cargar productos</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadProducts}>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="catalog-container">
      <div className="catalog-header">
        <h1 className="catalog-title">Catálogo de Productos</h1>
        <p className="catalog-subtitle">
          Encuentra lo que buscas y contacta con nosotros por WhatsApp
        </p>
      </div>

      <div className="catalog-search-section">
        <SearchBar onSearch={handleSearch} />
      </div>

      <FilterBar
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      {filteredProducts.length === 0 ? (
        <div className="catalog-empty">
          <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3>No se encontraron productos</h3>
          <p>Intenta con otros términos de búsqueda o filtros</p>
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('');
              setSortBy('default');
            }}
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <>
          <div className="catalog-results-info" ref={productsStartRef}>
            <p>
              Mostrando {indexOfFirstProduct + 1} - {Math.min(indexOfLastProduct, filteredProducts.length)} de {filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <div className="product-grid">
            {currentProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Controles de paginación */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button 
                className="pagination-btn" 
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                ← Anterior
              </button>
              
              <div className="pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => {
                  // Mostrar solo algunas páginas para evitar muchos botones
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                        onClick={() => goToPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 3 ||
                    pageNumber === currentPage + 3
                  ) {
                    return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button 
                className="pagination-btn" 
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductCatalog;

